import { c as _c } from "react/compiler-runtime";
import { safeLocalStorage } from "@/lib/storage";
import { useEffect } from "react";
import { toast } from "sonner";
const ANNOUNCED_ORGS_KEY = "blacksmith_announced_new_orgs";
const toastId = (login: string) => `new-org-${login}`;

// The toast outlives the page that showed it, so Switch resolves its
// destination at click time from the most recently mounted selector.
let latestOnSwitch: (login: string) => void = () => {};

/**
 * Tell the user an org they just installed the app on is connected, since
 * the dashboard stays on their current org after the GitHub round-trip.
 *
 * Exported separately so Storybook can render the exact production toast.
 */
export function showNewOrgToast(login: string, onSwitch: () => void): void {
  toast(`${login} is connected`, {
    id: toastId(login),
    description: "Switch to it to start running jobs on Blacksmith.",
    duration: Infinity,
    action: {
      label: "Switch",
      onClick: onSwitch
    },
    cancel: {
      label: "Dismiss",
      onClick: () => toast.dismiss(toastId(login))
    }
  });
}
function getAnnouncedOrgs(): Set<string> {
  const stored = safeLocalStorage.get(ANNOUNCED_ORGS_KEY);
  if (!stored) return new Set();
  try {
    return new Set(JSON.parse(stored));
  } catch {
    return new Set();
  }
}

/**
 * Toasts each newly installed org once, and retires the toast when the org
 * stops being new (e.g. the user switched to it). The org selector remounts
 * on every navigation, so announced orgs are persisted rather than tracked in
 * state. Pass undefined while org data is loading so an empty list doesn't
 * retire toasts for orgs that are still new.
 */
export function useNewOrgToast(newOrgLogins, onSwitch) {
  const $ = _c(6);
  let t0;
  let t1;
  if ($[0] !== onSwitch) {
    t0 = () => {
      latestOnSwitch = onSwitch;
    };
    t1 = [onSwitch];
    $[0] = onSwitch;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== newOrgLogins) {
    t2 = () => {
      if (!newOrgLogins) {
        return;
      }
      const announced = getAnnouncedOrgs();
      const stillNew = new Set(newOrgLogins);
      for (const login of announced) {
        if (!stillNew.has(login)) {
          toast.dismiss(toastId(login));
        }
      }
      const unannounced = newOrgLogins.filter(login_0 => !announced.has(login_0));
      if (unannounced.length === 0) {
        return;
      }
      for (const login_1 of unannounced) {
        showNewOrgToast(login_1, () => latestOnSwitch(login_1));
        announced.add(login_1);
      }
      safeLocalStorage.set(ANNOUNCED_ORGS_KEY, JSON.stringify(Array.from(announced)));
    };
    t3 = [newOrgLogins];
    $[3] = newOrgLogins;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
}